(() => {
  if (window.customElements.get('techyin-support-articles')) return;

  const indexCache = new Map();

  const fetchIndexPage = async (indexUrl, page) => {
    const url = new URL(indexUrl, window.location.origin);
    url.searchParams.set('page', String(page));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json, text/plain, */*' },
    });

    if (!response.ok) throw new Error(`Support index request failed: ${response.status}`);
    return JSON.parse(await response.text());
  };

  const loadArticleIndex = (indexUrl) => {
    const cacheKey = new URL(indexUrl, window.location.origin).toString();
    if (indexCache.has(cacheKey)) return indexCache.get(cacheKey);

    const request = fetchIndexPage(indexUrl, 1).then(async (firstPage) => {
      const articles = Array.isArray(firstPage.articles) ? firstPage.articles.slice() : [];
      const pageCount = Math.max(1, Number(firstPage.pages) || 1);

      if (pageCount > 1) {
        const remainingPages = await Promise.all(
          Array.from({ length: pageCount - 1 }, (_, index) => fetchIndexPage(indexUrl, index + 2)),
        );

        remainingPages.forEach((page) => {
          if (Array.isArray(page.articles)) articles.push(...page.articles);
        });
      }

      return articles;
    });

    indexCache.set(cacheKey, request);
    return request;
  };

  const articleTimestamp = (article) => {
    const value = article.reviewedAt || article.updatedAt || '';
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  class TechyinSupportArticles extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.load();
    }

    async load() {
      const indexUrl = this.dataset.indexUrl;
      const modelHandle = this.dataset.modelHandle;
      const modelName = this.dataset.modelName || '';
      const limit = Math.max(1, Number(this.dataset.limit) || 3);

      if (!indexUrl || !modelHandle) return;

      try {
        const index = await loadArticleIndex(indexUrl);
        const matchingArticles = index
          .filter((article) => {
            const models = Array.isArray(article.models) ? article.models : [];
            return models.includes(modelHandle) && String(article.lifecycle || '').toLowerCase() !== 'archived';
          })
          .sort((a, b) => {
            if (Boolean(a.featured) !== Boolean(b.featured)) return a.featured ? -1 : 1;
            return articleTimestamp(b) - articleTimestamp(a);
          });
        const articles = matchingArticles.slice(0, limit);

        if (!articles.length) {
          this.dataset.state = 'empty';
          return;
        }

        const results = this.querySelector('[data-support-article-results]');
        if (!results) return;

        articles.forEach((article) => results.appendChild(this.createArticle(article)));
        const viewAllLink = this.querySelector('[data-support-articles-all]');
        if (viewAllLink && matchingArticles.length > articles.length) {
          const labelTemplate = this.dataset.viewAllLabel || 'View all [count] [model] articles';
          const label = labelTemplate
            .replace('[count]', String(matchingArticles.length))
            .replace('[model]', modelName);
          viewAllLink.textContent = `${label} `;

          const arrow = document.createElement('span');
          arrow.textContent = '→';
          arrow.setAttribute('aria-hidden', 'true');
          viewAllLink.appendChild(arrow);
          viewAllLink.hidden = false;

          viewAllLink.addEventListener(
            'click',
            () => {
              matchingArticles
                .slice(articles.length)
                .forEach((article) => results.appendChild(this.createArticle(article)));
              this.dataset.count = String(matchingArticles.length);
              viewAllLink.setAttribute('aria-expanded', 'true');
              viewAllLink.hidden = true;
            },
            { once: true },
          );
        }
        this.dataset.count = String(articles.length);
        this.hidden = false;
        this.dataset.state = 'ready';
      } catch (error) {
        this.dataset.state = 'error';
        console.warn('Techyin Support article index could not be loaded.', error);
      }
    }

    createArticle(article) {
      const link = document.createElement('a');
      link.className = 'techyin-support-model__article';
      link.href = article.url;

      if (article.image) {
        const media = document.createElement('span');
        media.className = 'techyin-support-model__article-media';

        const image = document.createElement('img');
        image.src = article.image;
        image.alt = article.imageAlt || '';
        image.loading = 'lazy';
        image.width = 240;
        image.height = 150;
        media.appendChild(image);
        link.appendChild(media);
      } else {
        link.classList.add('techyin-support-model__article--no-image');
      }

      const copy = document.createElement('span');
      copy.className = 'techyin-support-model__article-copy';

      if (article.topic) {
        const topic = document.createElement('span');
        topic.className = 'techyin-support-model__article-topic';
        topic.textContent = article.topic;
        copy.appendChild(topic);
      }

      const title = document.createElement('span');
      title.className = 'techyin-support-model__article-title';
      title.textContent = article.title;
      copy.appendChild(title);

      if (article.excerpt) {
        const excerpt = document.createElement('span');
        excerpt.className = 'techyin-support-model__article-excerpt';
        excerpt.textContent = article.excerpt;
        copy.appendChild(excerpt);
      }

      const action = document.createElement('span');
      action.className = 'techyin-support-model__article-action';
      action.textContent = 'View guide';
      action.setAttribute('aria-hidden', 'true');

      const arrow = document.createElement('span');
      arrow.textContent = '→';
      action.appendChild(arrow);

      link.append(copy, action);
      return link;
    }
  }

  window.customElements.define('techyin-support-articles', TechyinSupportArticles);

  if (!window.customElements.get('techyin-support-search-results')) {
    class TechyinSupportSearchResults extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';
        this.load();
      }

      async load() {
        const indexUrl = this.dataset.indexUrl;
        const query = (this.dataset.query || '').toLowerCase().trim();
        const results = this.querySelector('[data-support-index-results]');
        const nativeResults = document.querySelector('[data-support-native-results]');
        if (!indexUrl || !results || !query) return;

        try {
          const index = await loadArticleIndex(indexUrl);
          const matchingArticles = index
            .filter((article) => {
              const lifecycle = String(article.lifecycle || '').toLowerCase();
              const haystack = String(article.searchText || `${article.title || ''} ${article.excerpt || ''}`).toLowerCase();
              return lifecycle !== 'archived' && haystack.includes(query);
            })
            .sort((a, b) => {
              if (Boolean(a.featured) !== Boolean(b.featured)) return a.featured ? -1 : 1;
              const aLegacy = String(a.lifecycle || '').toLowerCase() === 'legacy';
              const bLegacy = String(b.lifecycle || '').toLowerCase() === 'legacy';
              if (aLegacy !== bLegacy) return aLegacy ? 1 : -1;
              return articleTimestamp(b) - articleTimestamp(a);
            });

          if (nativeResults) nativeResults.hidden = true;
          results.replaceChildren();

          const meta = document.createElement('p');
          meta.className = 'techyin-support-search__meta';
          meta.textContent = matchingArticles.length === 1 ? '1 support article found.' : `${matchingArticles.length} support articles found.`;
          results.appendChild(meta);

          if (matchingArticles.length) {
            const grid = document.createElement('div');
            grid.className = 'techyin-support-search__grid';
            matchingArticles.forEach((article) => grid.appendChild(this.createCard(article)));
            results.appendChild(grid);
          } else {
            results.appendChild(this.createEmptyState());
          }

          this.hidden = false;
          this.dataset.state = 'ready';
        } catch (error) {
          this.dataset.state = 'error';
          console.warn('Techyin Support search index could not be loaded.', error);
        }
      }

      createCard(article) {
        const card = document.createElement('article');
        card.className = 'techyin-support-search-card';

        const link = document.createElement('a');
        link.className = 'techyin-support-search-card__link';
        link.href = article.url;
        link.setAttribute('aria-label', article.title || 'View support article');

        const body = document.createElement('div');
        body.className = 'techyin-support-search-card__body';

        if (article.topic) {
          const badge = document.createElement('span');
          badge.className = 'techyin-support-search-card__badge';
          badge.textContent = article.topic;
          body.appendChild(badge);
        }

        const title = document.createElement('h2');
        title.className = 'techyin-support-search-card__title';
        title.textContent = article.title || '';
        body.appendChild(title);

        const modelNames = Array.isArray(article.modelNames) ? article.modelNames : [];
        if (modelNames.length) {
          const models = document.createElement('p');
          models.className = 'techyin-support-search-card__models';
          models.textContent = `Applies to ${modelNames.join(' · ')}`;
          body.appendChild(models);
        }

        if (article.excerpt) {
          const excerpt = document.createElement('p');
          excerpt.className = 'techyin-support-search-card__excerpt';
          excerpt.textContent = article.excerpt;
          body.appendChild(excerpt);
        }

        const action = document.createElement('span');
        action.className = 'techyin-support-search-card__cta';
        action.textContent = String(article.templateSuffix || '').includes('short') ? 'Watch guide →' : 'View guide →';
        body.appendChild(action);

        card.append(link, body);
        return card;
      }

      createEmptyState() {
        const empty = document.createElement('div');
        empty.className = 'techyin-support-search__empty';

        const heading = document.createElement('h2');
        heading.textContent = 'No matching support articles';
        const text = document.createElement('p');
        text.textContent = 'Try a shorter keyword, browse all support articles, or contact Techyin Support if the issue is urgent.';
        const actions = document.createElement('div');
        actions.className = 'techyin-support-search__empty-actions';

        const browse = document.createElement('a');
        browse.href = '/blogs/support/';
        browse.textContent = 'Browse Support Articles';
        const contact = document.createElement('a');
        contact.href = '/pages/contact-support';
        contact.textContent = 'Contact Support';

        actions.append(browse, contact);
        empty.append(heading, text, actions);
        return empty;
      }
    }

    window.customElements.define('techyin-support-search-results', TechyinSupportSearchResults);
  }
})();
